import { useMemo } from 'react';

import * as api from '@gitbook/api-client';
import {
    compareSiteTypes,
    findRequiredPlanForFeature,
    getMinimumSiteSubscriptionForFeatures,
    getSiteProductDefinition,
    getUpgradeRequirement,
    isSiteFeatureIncludedInPlan,
} from '@gitbook/app-pricing';
import { useDateRefresh } from '@gitbook/util-react-hooks';

import { useCurrentOrganization } from '../organization';
import { calculateDaysDifference } from './common';

export type OrgTrialDetails = {
    status: api.BillingTrialStatus;
    daysLeft?: number;
};

/**
 * A hook to return details about an organization trial.
 */
export function useTrial(props: { organization: api.Organization }): OrgTrialDetails {
    const { organization } = props;
    const daysLeft = useRemainingTrialDays(organization);

    return useMemo(
        () => ({ status: organization.trial.status, daysLeft }),
        [organization.trial.status, daysLeft]
    );
}

/**
 * Return the plan covering an organization feature during the active trial.
 */
export function getTrialingOrganizationFeaturePlan(
    organization: api.Organization,
    featureKey: Exclude<api.OrganizationFeature, api.OrganizationFeature.MaxMembers>
): string | null {
    // A feature the plan still blocks, itself or through a dependency, is not covered by the trial.
    if (
        organization.trial.status !== api.BillingTrialStatus.Active ||
        getUpgradeRequirement({ organization, featureKey })
    ) {
        return null;
    }

    return findRequiredPlanForFeature(featureKey)?.title ?? null;
}

/**
 * Return the plan covering an organization feature during the active trial.
 */
export function useTrialingOrganizationFeaturePlan(
    featureKey: Exclude<api.OrganizationFeature, api.OrganizationFeature.MaxMembers>
): string | null {
    const organization = useCurrentOrganization();

    return getTrialingOrganizationFeaturePlan(organization, featureKey);
}

/**
 * Return the paid plan covering a site feature during the active trial.
 */
export function useTrialingSiteFeaturePlan(
    site: api.Site | undefined,
    featureKey: api.SiteFeatureKey
): string | null {
    const organization = useCurrentOrganization();

    if (
        !site ||
        organization.trial.status !== api.BillingTrialStatus.Active ||
        getUpgradeRequirement({ organization, site, featureKey })
    ) {
        return null;
    }

    const currentSiteType = site.appliedType ?? site.type;
    const existingFeature = site.features.find((feature) => feature.id === featureKey);
    if (!isSiteFeatureIncludedInPlan(featureKey, currentSiteType)) {
        return null;
    }

    const requiredSiteType =
        existingFeature?.plan ??
        getMinimumSiteSubscriptionForFeatures({
            featureKeys: [featureKey],
            defaultSiteType: currentSiteType,
        });

    if (!requiredSiteType || compareSiteTypes(requiredSiteType, api.SiteType.Basic) <= 0) {
        return null;
    }

    return getSiteProductDefinition(requiredSiteType)?.title ?? null;
}

/**
 * A hook to get the number of days (rounded up) remaining on the free trial with auto-refresh.
 */
function useRemainingTrialDays(organization: api.Organization): number | undefined {
    const trialEnd = organization.trial.endDate ? new Date(organization.trial.endDate) : undefined;

    useDateRefresh(trialEnd ?? null);

    if (!trialEnd || organization.trial.status !== api.BillingTrialStatus.Active) {
        return undefined;
    }

    return calculateDaysDifference(new Date(), trialEnd);
}
